import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";

// Canvas preview-safe fallback.
// Enclopedia本番環境では以下に置き換えてください。
// import { headRewrite } from "../function/headRewrite";
const headRewrite = () => {};

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      borderRight: "none",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.18,
    letterSpacing: "-0.08em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.8vw, 34px)",
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 700,
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  noticeBody: {
    fontSize: 13,
    lineHeight: 1.9,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#111",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 540,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 390,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 22,
    width: 270,
    height: 270,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 24,
    top: 44,
    width: 190,
    height: 190,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  heroMock: {
    position: "relative",
    width: "76%",
    maxWidth: 430,
    aspectRatio: "1 / 1",
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    overflow: "hidden",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "84%",
      maxWidth: 320,
    },
  },
  heroMockImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  heroMockLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  heroMockTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(24px, 3vw, 36px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  thesisCard: {
    border: "1px solid #111",
    minHeight: 315,
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.72,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  valuationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  valuationCard: {
    border: "1px solid #111",
    minHeight: 315,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
  },
  brandGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  brandItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 192,
  },
  brandTag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.4),
  },
  brandTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  listBlock: {
    borderTop: "1px solid #111",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "72px 280px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  listIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  listTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.72,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  flowGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(2.5),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  flowCard: {
    border: "1px solid #111",
    minHeight: 255,
    padding: theme.spacing(2.8),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  flowStep: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  flowTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 22,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  assuranceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  assuranceItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 180,
  },
  assuranceTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
    marginBottom: theme.spacing(1.5),
  },
  noteBox: {
    marginTop: theme.spacing(4),
    border: "1px solid #111",
    padding: theme.spacing(3),
    background: "rgba(255,255,255,0.18)",
  },
  faqGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  faqItem: {
    border: "1px solid #111",
    padding: theme.spacing(3),
  },
  faqQuestion: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  finalButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2.5),
    flexWrap: "wrap",
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  indexHero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
  },
  indexIntro: {
    maxWidth: 820,
  },
  articleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  articleCard: {
    border: "1px solid #111",
    minHeight: 330,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    color: "#111",
    textDecoration: "none",
    transition: "background 0.2s ease, color 0.2s ease",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  articleCategory: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  articleTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  articleCardBody: {
    fontSize: 14,
    lineHeight: 2,
  },
  articleArrow: {
    marginTop: theme.spacing(3),
    fontWeight: 700,
    fontSize: 14,
  },
  notFound: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(8, 0),
  },
}));

const brandBuybackArticles = [
  {
    slug: "comme-des-garcons",
    category: "BRAND BUYBACK",
    listTitle: "コムデギャルソン買取",
    listDescription:
      "シーズン背景、コレクション性、希少性まで見て査定するEnclopediaのブランド別買取ページ。",
    seo: {
      title:
        "コムデギャルソン買取｜アーカイブ価値まで見て査定するEnclopedia",
      description:
        "COMME des GARÇONSの買取ならEnclopediaへ。シーズン背景、コレクション性、希少性まで見て査定します。高価買取を目指しつつ、まずはLINE事前査定から宅配買取へご案内します。",
      keyword:
        "コムデギャルソン 買取,COMME des GARCONS 買取,コムデギャルソン 高価買取,コムデギャルソン 宅配買取,COMME des GARÇONS HOMME PLUS 買取,JUNYA WATANABE 買取,noir kei ninomiya 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/comme-des-garcons",
    },
    hero: {
      label: "COMME DES GARÇONS BUYBACK",
      number: "01 / BUYBACK",
      titleLines: [
        "コムデギャルソン買取。",
        "アーカイブ価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、COMME des GARÇONSをブランド名だけで判断しません。\nシーズン背景、コレクション性、希少性まで確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで事前にご相談いただき、納得感のある宅配買取へつなげます。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref: "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "buyback",
        "archive valuation",
        "high-value purchase",
        "comme des garçons",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: ["ブランド名ではなく、", "服の背景まで見る。"],
      image: "media/archive_fashion/buyback1.png",
      imageAlt: "コムデギャルソン買取のメイン画像",
    },
    thesis: {
      text:
        "高価買取を目指しながら、シーズン背景とアーカイブ価値を査定に反映する。",
      keywords: ["season", "collection", "rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: ["コムデギャルソンの価値は、", "相場表だけでは測れない。"],
      body:
        "新しいシーズンの方が高値になりやすい場面はあります。\nただし、Enclopediaは年代だけで一律に判断しません。\nその服がどのシーズンに属し、どのようなコレクション性を持ち、いまどれほど希少かを見ながら、アイテム単位で査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: ["Enclopediaが重視する、", "3つの査定視点。"],
      items: [
        {
          number: "01",
          title: "シーズン背景を見る",
          text:
            "単にブランド名や型番だけで判断せず、どの時期の思想やテーマの中で生まれた服かを確認します。",
        },
        {
          number: "02",
          title: "コレクション性を見る",
          text:
            "ランウェイ登場品、シーズンの象徴性が強いデザイン、当時のテーマをよく示すピースは丁寧に評価します。",
        },
        {
          number: "03",
          title: "希少性を見る",
          text:
            "市場流通の少なさ、サイズ、保存状態、再入手難度まで含めて、個々のアイテム単位で査定します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET LINES",
      titleLines: ["COMME des GARÇONS全般を、", "幅広く査定します。"],
      items: [
        {
          tag: "LINE 01",
          title: "COMME des GARÇONS",
          text:
            "本ラインをはじめ、ブランドの核となるアーカイブや象徴的なコレクションピースを査定します。",
        },
        {
          tag: "LINE 02",
          title: "COMME des GARÇONS HOMME PLUS",
          text:
            "コレクション性が高く、Enclopediaが特に注力して見たいラインです。近年シーズンから過去作まで個別評価します。",
        },
        {
          tag: "LINE 03",
          title: "COMME des GARÇONS HOMME / COMME des GARÇONS HOMME DEUX",
          text:
            "日常着に近いラインでも、素材使い・年代・ディテールによって価値が変わるため丁寧に見ます。",
        },
        {
          tag: "LINE 04",
          title: "COMME des GARÇONS SHIRT / COMME des GARÇONS COMME des GARÇONS",
          text:
            "シャツ、ワンピース、女性向けラインも対象。ブランドの個性が強い品は積極的に査定します。",
        },
        {
          tag: "LINE 05",
          title: "JUNYA WATANABE",
          text:
            "構築性やコラボレーション性が高いアイテムを中心に、背景を踏まえて査定します。",
        },
        {
          tag: "LINE 06",
          title: "noir kei ninomiya",
          text:
            "カッティング、構造、加工が際立つ作品を評価。現行・過去シーズン問わずご相談いただけます。",
        },
        {
          tag: "LINE 07",
          title: "PLAY COMME des GARÇONS / Wallet等",
          text:
            "幅広いラインに対応します。ただし査定額は市場性・状態・需要により個別に判断します。",
        },
        {
          tag: "LINE 08",
          title: "その他関連ライン",
          text:
            "tricot、robe de chambre、GANRYU、BLACKなど、関連ブランドも内容に応じて査定します。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: ["高価買取につながりやすい、", "コムデギャルソン。"],
      intro:
        "査定額は状態や市場性で変動しますが、Enclopediaでは特に以下のような服を丁寧に見ます。",
      items: [
        {
          index: "01",
          title: "近年シーズンの人気アイテム",
          text:
            "現時点では比較的新しいシーズンの方が相場上高値になりやすい傾向があります。特に人気の強いアウター、ジャケット、ワンピースはご相談ください。",
        },
        {
          index: "02",
          title: "ランウェイ登場品・シーズンテーマを象徴する服",
          text:
            "コレクション全体の思想を担うルック、象徴性の強い柄や構造、ブランドの文脈が濃いアイテムは評価対象です。",
        },
        {
          index: "03",
          title: "希少アーカイブ・再入手しにくいピース",
          text:
            "古いだけで高くなるわけではありませんが、流通量が少なく、今なお評価される作品は個別に価値を見ます。",
        },
        {
          index: "04",
          title: "再構築・縮絨・変形・立体裁断などの代表的ディテール",
          text:
            "COMME des GARÇONSらしさが強く現れる技法や造形は、アイテムそのものの魅力として査定に反映します。",
        },
      ],
    },
    quote: {
      textLines: ["売れる服としてではなく、", "次へ受け継ぐ価値のある服として見る。"],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: ["一般的な査定で、", "見落とされやすい価値。"],
      intro:
        "Enclopediaは、ブランド古着としての流通性だけでなく、アーカイブとしての文脈も確認します。",
      items: [
        {
          index: "01",
          title: "年代だけで一律に見ない",
          text:
            "“古いから高い”“新しいから高い”ではなく、その服がどのような評価軸を持つかを確認します。",
        },
        {
          index: "02",
          title: "型番相場だけで終わらせない",
          text:
            "過去の取引価格だけでなく、今の需要、コレクション文脈、海外評価まで含めて個別に考えます。",
        },
        {
          index: "03",
          title: "売れるかだけでなく、残す価値を見る",
          text:
            "Enclopediaはアーカイブをレンタル・共有するサービスでもあります。次の着用者へ渡す価値がある服を丁寧に受け取ります。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: ["LINE事前査定から、", "宅配買取へ。"],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。宅配買取へ進む前に、おおまかな査定可否をご案内します。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: ["相談しやすく、", "進めやすい宅配買取。"],
      intro:
        "LINEで相談してから進められるため、買取成立までの不安と手間を減らせます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、無駄な発送や手間を減らせます。",
        },
        {
          title: "送料は無料",
          text: "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: ["コムデギャルソン買取で、", "よくあるご質問。"],
      items: [
        {
          question: "どのラインのコムデギャルソンを買取していますか？",
          answer:
            "COMME des GARÇONS全般に対応しています。本ライン、HOMME PLUS、HOMME、HOMME DEUX、SHIRT、COMME COMME、JUNYA WATANABE、noir kei ninomiyaなど幅広くご相談いただけます。",
        },
        {
          question: "古いアーカイブほど高く買い取ってもらえますか？",
          answer:
            "年代だけで一律には判断しません。現在は近年シーズンの方が高値になりやすい場合もありますが、過去作でもコレクション性・希少性・需要が高いものは個別に評価します。",
        },
        {
          question: "LINE査定をせずに宅配買取を申し込めますか？",
          answer:
            "申込自体は可能ですが、事前にLINE査定をご利用いただくことで、査定可否や大まかな方向性を確認しやすくなります。実務上もLINE事前査定をおすすめしています。",
        },
        {
          question: "キャンセルした場合、返送料はかかりますか？",
          answer:
            "基本的には無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
        },
        {
          question: "宅配キットは必ず使う必要がありますか？",
          answer:
            "必須ではありません。宅配キットは選択制のため、ご自身で梱包いただける場合はそのまま発送いただけます。",
        },
        {
          question: "査定では何を見ていますか？",
          answer:
            "状態や市場相場に加え、シーズン背景、コレクション性、希少性、現在の需要を総合して判断します。",
        },
      ],
    },
    finalCta: {
      titleLines: ["まずはLINEで、", "コムデギャルソンの価値をご相談ください。"],
      body:
        "宅配買取へ進む前に、写真で事前確認。\n背景まで見て査定するEnclopediaが、次の流通へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref: "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "sacai",
    category: "BRAND BUYBACK",
    listTitle: "sacai買取",
    listDescription:
      "異素材切替、ドッキング、コラボレーションの価値まで見て査定するEnclopediaのsacai専用買取ページ。",
    seo: {
      title:
        "sacai買取｜異素材切替・コラボ・アーカイブ価値まで見て査定するEnclopedia",
      description:
        "sacaiの買取ならEnclopediaへ。異素材切替、ドッキング構造、コラボレーション、シーズン性、希少性まで見て査定します。まずはLINE事前査定から宅配買取へご案内します。",
      keyword:
        "sacai 買取,サカイ 買取,sacai 高価買取,sacai 宅配買取,sacai アーカイブ 買取,sacai コラボ 買取,sacai Nike 買取,sacai WTAPS 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/sacai",
    },
    hero: {
      label: "SACAI BUYBACK",
      number: "02 / BUYBACK",
      titleLines: [
        "sacai買取。",
        "構造の価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、sacaiをブランド名だけで一律に判断しません。\n異素材切替、ドッキング構造、シーズン背景、コラボレーション性まで確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで事前にご相談いただき、納得感のある宅配買取へつなげます。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "buyback",
        "hybrid construction",
        "collaboration value",
        "sacai",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: ["素材の切替だけでなく、", "構造の背景まで見る。"],
      image: "media/archive_fashion/buyback2.png",
      imageAlt: "sacai買取のメイン画像",
    },
    thesis: {
      text:
        "sacaiの価値は、人気だけでなく、服の構造・シーズン性・協業背景まで見て判断する。",
      keywords: ["construction", "season", "rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "sacaiの価値は、",
        "“異素材ミックス”の一言では終わらない。",
      ],
      body:
        "sacaiは、見慣れた服の構造を組み替えることで新しい輪郭を生み出してきたブランドです。\nニットと布帛、テーラードとミリタリー、スカートとプリーツなど、異なる要素を一着の中で成立させる設計に特徴があります。\nEnclopediaでは、単なる人気ブランドとしてではなく、その服が持つ構造的な価値と市場性をあわせて見ます。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: ["Enclopediaが重視する、", "3つの査定視点。"],
      items: [
        {
          number: "01",
          title: "構造とデザイン性を見る",
          text:
            "異素材切替、ドッキング、レイヤード表現など、sacaiらしさがどの程度アイテムに表れているかを確認します。",
        },
        {
          number: "02",
          title: "シーズン性と希少性を見る",
          text:
            "どの時期の作品か、流通量がどの程度か、再入手しにくいピースかを踏まえて個別に判断します。",
        },
        {
          number: "03",
          title: "コラボレーション価値を見る",
          text:
            "WTAPS、NIKEをはじめとした協業品は、通常ラインとは異なる需要や評価軸があるため、背景を見て査定します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: ["sacai全般を、", "幅広く査定します。"],
      items: [
        {
          tag: "ITEM 01",
          title: "sacai レディース",
          text:
            "ワンピース、スカート、ニット、ジャケット、コートなど、ウィメンズの主要アイテムを幅広く査定します。",
        },
        {
          tag: "ITEM 02",
          title: "sacai メンズ",
          text:
            "ブルゾン、ジャケット、パンツ、ニット、シャツなど、構造性の強いメンズアイテムも積極的に見ます。",
        },
        {
          tag: "ITEM 03",
          title: "異素材切替・ドッキング系",
          text:
            "sacaiの代表性が強い切替ニット、ドッキングアウター、レイヤードスカートは特に丁寧に査定します。",
        },
        {
          tag: "ITEM 04",
          title: "コラボレーションアイテム",
          text:
            "sacai × WTAPS、sacai × NIKEなど、需要の強い協業品は内容に応じて個別評価します。",
        },
        {
          tag: "ITEM 05",
          title: "アウター・ジャケット",
          text:
            "MA-1、トレンチ、テーラード、ミリタリー由来のアウターなど、sacaiらしい構造の出るアイテムを見ます。",
        },
        {
          tag: "ITEM 06",
          title: "ニット・スウェット",
          text:
            "異素材の接続やレイヤード表現が分かりやすいトップス類も査定対象です。",
        },
        {
          tag: "ITEM 07",
          title: "スカート・ワンピース",
          text:
            "プリーツ、布帛、異素材を組み合わせたボトムスやドレスは、sacaiらしさが評価に結びつきやすい領域です。",
        },
        {
          tag: "ITEM 08",
          title: "バッグ・小物類",
          text:
            "アイテムにより査定可能です。コレクション性や市場需要を確認して個別にご案内します。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: ["高価買取につながりやすい、", "sacaiのアイテム。"],
      intro:
        "査定額は状態や市場性で変動しますが、Enclopediaでは特に以下のようなsacaiを丁寧に見ます。",
      items: [
        {
          index: "01",
          title: "異素材切替・ドッキング構造が際立つ代表作",
          text:
            "ニット、ジャケット、スカート、ワンピースなど、sacaiの設計思想が明確に表れたアイテムは評価対象です。",
        },
        {
          index: "02",
          title: "人気シーズンの象徴的なアイテム",
          text:
            "その時期のコレクション性を強く示すルックや、継続的に需要のあるアウター類は丁寧に査定します。",
        },
        {
          index: "03",
          title: "sacai × WTAPS、sacai × NIKEなどのコラボレーション",
          text:
            "ブランド同士の文脈が重なる協業品は、市場評価が通常ラインと異なる場合があるため、個別に価値を見ます。",
        },
        {
          index: "04",
          title: "状態良好なアーカイブ・入手困難品",
          text:
            "過去作の中でも流通量が少なく、現在も需要が続いているものは、年代だけでなく希少性まで含めて判断します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“人気ブランド”としてではなく、",
        "構造を持った服として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: ["一般的な査定で、", "見落とされやすいsacaiの価値。"],
      intro:
        "Enclopediaは、単純な型番相場だけでなく、そのアイテムがsacaiの中でどのような意味を持つかも確認します。",
      items: [
        {
          index: "01",
          title: "異素材切替を“装飾”として片付けない",
          text:
            "sacaiの切替や重なりは、ブランドの本質に関わる設計です。どの程度構造性が強いかを見ます。",
        },
        {
          index: "02",
          title: "コラボ品を一括評価しない",
          text:
            "コラボレーションは、相手ブランドや発売時期、現在の需要で評価が変わります。個別に確認します。",
        },
        {
          index: "03",
          title: "着用価値と再流通価値をあわせて見る",
          text:
            "Enclopediaはレンタル事業も行うため、次に着る人へ渡る価値があるかという視点も持って査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: ["LINE事前査定から、", "宅配買取へ。"],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。宅配買取へ進む前に、おおまかな査定可否をご案内します。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: ["相談しやすく、", "進めやすいsacai買取。"],
      intro:
        "LINEで相談してから進められるため、買取成立までの不安と手間を減らせます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、無駄な発送や手間を減らせます。",
        },
        {
          title: "送料は無料",
          text: "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: ["sacai買取で、", "よくあるご質問。"],
      items: [
        {
          question: "どのsacaiアイテムを買取していますか？",
          answer:
            "ジャケット、コート、ニット、スカート、ワンピース、パンツ、コラボレーション品など幅広くご相談いただけます。",
        },
        {
          question: "sacai × WTAPSやsacai × NIKEも査定できますか？",
          answer:
            "はい。コラボレーション背景や市場需要を確認し、通常ラインとは分けて個別に評価します。",
        },
        {
          question: "古いsacaiの方が高くなりますか？",
          answer:
            "年代だけで一律には判断しません。現在の需要、シーズン性、希少性、状態を総合して査定します。",
        },
        {
          question: "異素材切替のアイテムは評価されやすいですか？",
          answer:
            "sacaiらしさが強く表れるため注目して見ます。ただし、査定額は状態・需要・アイテムごとの流通性を踏まえて判断します。",
        },
        {
          question: "LINE査定をせずに宅配買取を申し込めますか？",
          answer:
            "申込自体は可能ですが、事前にLINE査定をご利用いただくことで、査定可否や大まかな方向性を確認しやすくなります。",
        },
        {
          question: "宅配キットは必ず使う必要がありますか？",
          answer:
            "必須ではありません。宅配キットは選択制のため、ご自身で梱包いただける場合はそのまま発送いただけます。",
        },
      ],
    },
    finalCta: {
      titleLines: ["まずはLINEで、", "sacaiの価値をご相談ください。"],
      body:
        "異素材切替、コラボレーション、シーズン背景まで確認。\nEnclopediaが、次の流通へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
];

function renderLines(lines) {
  return lines.map((line, index) => (
    <React.Fragment key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}

function Seo({ article, canonicalUrl }) {
  const { title, description, keyword } = article.seo;
  const ogImage = article.hero.image
    ? `${process.env.REACT_APP_IMAGE_URL || ""}${article.hero.image}`
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "ブランド買取",
            "宅配買取",
            article.listTitle,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Brand Buyback",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/brand_buyback`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.listTitle,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.faq.items.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        })}
      </script>
    </Helmet>
  );
}

function IndexSeo() {
    const title =
    "ブランド買取｜アーカイブ価値まで見て査定するEnclopedia";

  const description =
    "Enclopediaのブランド買取ページ一覧。デザイナーズブランドのシーズン背景・コレクション性・希少性まで見て査定し、LINE事前査定から宅配買取までご案内します。";

  const keyword =
    "ブランド別買取,デザイナーズブランド 買取,アーカイブ買取,ブランド古着 買取,宅配買取,LINE査定,Enclopedia";

  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/brand_buyback`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Enclopedia",
            url: typeof window !== "undefined" ? window.location.origin : "",
          },
        })}
      </script>
    </Helmet>
  );
}

function BrandBuybackIndex({ classes }) {
  return (
    <div className={classes.root}>
      <IndexSeo />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.indexHero}>
            <div className={classes.indexIntro}>
              <div className={classes.labelRow}>
                <span>BRAND BUYBACK</span>
                <span className={classes.labelLine} />
              </div>
              <Typography variant="h1" component="h1" className={classes.heroTitle}>
                ブランドごとの価値を、
                <br />
                文脈まで見て査定する。
              </Typography>
              <Typography component="p" className={classes.heroBody}>
                Enclopediaでは、シーズン背景・コレクション性・希少性を重視したブランド別買取ページを用意しています。
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.articleGrid}>
              {brandBuybackArticles.map((article) => (
                <a
                  href={`/brand_buyback/${article.slug}`}
                  className={classes.articleCard}
                  key={article.slug}
                >
                  <div>
                    <div className={classes.articleCategory}>{article.category}</div>
                    <Typography component="h2" className={classes.articleTitle}>
                      {article.listTitle}
                    </Typography>
                    <Typography component="p" className={classes.articleCardBody}>
                      {article.listDescription}
                    </Typography>
                  </div>
                  <div className={classes.articleArrow}>ページを見る →</div>
                </a>
              ))}
            </div>
          </section>
        </div>
      </Container>
    </div>
  );
}

function BrandBuybackNotFound({ classes }) {
  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.notFound}>
          <div>
            <Typography component="h1" className={classes.heading}>
              買取ページが見つかりませんでした。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/brand_buyback">
              ブランド別買取一覧へ戻る
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function BrandBuybackDetail({ classes, article }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${article.seo.canonicalPath}`;
  const { hero, thesis } = article;

  return (
    <div className={classes.root}>
      <Seo article={article} canonicalUrl={canonicalUrl} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{hero.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {renderLines(hero.titleLines)}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {hero.body}
                </Typography>
                <div className={classes.buttonRow}>
                  <Button className={classes.primaryButton} component="a" href={hero.primaryCtaHref}>
                    {hero.primaryCtaLabel}
                  </Button>
                  <Button className={classes.secondaryButton} component="a" href={hero.secondaryCtaHref}>
                    {hero.secondaryCtaLabel}
                  </Button>
                </div>
                <div className={classes.keywordLine}>
                  {hero.keywords.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < hero.keywords.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{hero.number}</div>
                  <div className={classes.heroMock}>
                    <img
                      src={`${process.env.REACT_APP_IMAGE_URL || ""}${hero.image}`}
                      alt={hero.imageAlt}
                      className={classes.heroMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>{article.intro.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.intro.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.intro.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {thesis.text}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    {thesis.keywords.map((keyword, index) => (
                      <React.Fragment key={keyword}>
                        <span>{keyword}</span>
                        {index < thesis.keywords.length - 1 && <span>/</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.valuation.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.valuation.titleLines)}
            </Typography>
            <div className={classes.valuationGrid}>
              {article.valuation.items.map((item) => (
                <div className={classes.valuationCard} key={item.number}>
                  <div>
                    <div className={classes.cardNumber}>{item.number}</div>
                    <div className={classes.cardTitle}>{item.title}</div>
                    <div className={classes.smallRule} />
                    <Typography component="p" className={classes.body}>
                      {item.text}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.targetLines.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.targetLines.titleLines)}
            </Typography>
            <div className={classes.brandGrid}>
              {article.targetLines.items.map((item) => (
                <div className={classes.brandItem} key={item.title}>
                  <div className={classes.brandTag}>{item.tag}</div>
                  <Typography component="h3" className={classes.brandTitle}>
                    {item.title}
                  </Typography>
                  <Typography component="p" className={classes.noteBody}>
                    {item.text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.highValueItems.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.highValueItems.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.highValueItems.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {article.highValueItems.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {renderLines(article.quote.textLines)}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.assessmentDifference.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.assessmentDifference.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.assessmentDifference.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {article.assessmentDifference.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.flow.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.flow.titleLines)}
            </Typography>
            <div className={classes.flowGrid}>
              {article.flow.items.map((item) => (
                <div className={classes.flowCard} key={item.step}>
                  <div>
                    <div className={classes.flowStep}>{item.step}</div>
                    <Typography component="h3" className={classes.flowTitle}>
                      {item.title}
                    </Typography>
                    <Typography component="p" className={classes.noteBody}>
                      {item.text}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.assurance.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.assurance.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.assurance.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.assuranceGrid}>
                  {article.assurance.items.map((item) => (
                    <div className={classes.assuranceItem} key={item.title}>
                      <div className={classes.assuranceTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
                <div className={classes.noteBox}>
                  <Typography component="p" className={classes.noticeBody}>
                    {article.assurance.notice}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section} id="faq">
            <div className={classes.labelRow}>
              <span>{article.faq.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.faq.titleLines)}
            </Typography>
            <div className={classes.faqGrid}>
              {article.faq.items.map((faq) => (
                <div className={classes.faqItem} key={faq.question}>
                  <div className={classes.faqQuestion}>{faq.question}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {faq.answer}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="h2" className={classes.finalTitle}>
              {renderLines(article.finalCta.titleLines)}
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              {article.finalCta.body}
            </Typography>
            <div className={classes.finalButtons}>
              <Button className={classes.primaryButton} component="a" href={article.finalCta.primaryHref}>
                {article.finalCta.primaryLabel}
              </Button>
              <Button className={classes.secondaryButton} component="a" href={article.finalCta.secondaryHref}>
                {article.finalCta.secondaryLabel}
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default function BrandBuybackScreen() {
  const classes = useStyles();
  const { slug } = useParams();

  if (!slug) {
    return <BrandBuybackIndex classes={classes} />;
  }

  const article = brandBuybackArticles.find((item) => item.slug === slug);

  if (!article) {
    return <BrandBuybackNotFound classes={classes} />;
  }

  return <BrandBuybackDetail classes={classes} article={article} />;
}
